const { DEFAULT_PROFILE_IMG, INTERESTS } = require('utils/constants');
const { validateProfilePhotoFile } = require('utils/profilePhotoCapture');
const { runProfanityCheck } = require('utils/profanityFilter');
const UserInputsConfig = require('./formConstants');
const FormValidationUtils = require('hooks/useFormPrimitives/validation');

const {
    MB_IN_BYTES
} = UserInputsConfig;

const UserInputsValidationUtils = module.exports = {
    ...FormValidationUtils
};

const userInputsValidators = {
    profanity({
        config,
        context,
        definition,
        value
    }) {

        if (!context.props?.useProfanityFilter) {
            return null;
        }

        const valueToCheck = FormValidationUtils.getContextValue({
            config,
            context,
            definition,
            value
        });

        if (!valueToCheck) {
            return null;
        }

        const {
            passesCheck,
            badPhrases = []
        } = runProfanityCheck(valueToCheck);

        if (passesCheck && !badPhrases.length) {
            return null;
        }

        return config.message
            || definition.profanityMessage
            || `We detected inappropriate language in this field which violates community standards. The following word(s) are not allowed: ${badPhrases.join(', ')}.`;
    },

    file({
        config,
        value
    }) {

        const validator = config.validator;

        if (typeof validator === 'function') {
            return validator({
                value,
                config
            });
        }

        if (validator === 'profilePhoto' || config.profilePhoto) {
            return validateProfilePhotoFile(value, {
                mbInBytes: config.mbInBytes || MB_IN_BYTES
            });
        }

        return null;
    },

    profilePhoto({
        config,
        context,
        definition,
        value
    }) {

        const fileError = UserInputsValidationUtils.validators.file({
            config: {
                ...config,
                validator: 'profilePhoto'
            },
            context,
            definition,
            value
        });

        if (fileError) {
            return fileError;
        }

        const rolePermissions = context.props?.rolePermissions || {};
        const croppedPicture = context.rendererState?.croppedPicture;
        const originalPicture = context.rendererState?.originalPicture
            || context.props?.signupDetails?.croppedPicture
            || context.props?.signupDetails?.picture;
        const defaultPhoto = config.defaultPhoto || rolePermissions.defaultPhoto || DEFAULT_PROFILE_IMG;
        const userHasDefaultProfilePic = croppedPicture === defaultPhoto;
        const hasExistingProfilePic = !!originalPicture && originalPicture !== defaultPhoto;

        if (
            rolePermissions.profilePicRequired
            && !value
            && !hasExistingProfilePic
            && (userHasDefaultProfilePic || (!croppedPicture && !context.getFieldValue(config.previewFieldName || 'picPreviewURL')))
        ) {
            return config.requiredMessage || FormValidationUtils.buildRequiredMessage({
                ...definition,
                label: config.label || definition.label || 'Profile pic'
            });
        }

        return null;
    },

    ageRange({
        config,
        context,
        definition,
        value
    }) {

        if (config.signupOnly !== false && !context.isSignupPage) {
            return null;
        }

        const refName = config.refName || definition.refName || definition.name;

        if (config.requireMountedRef !== false && !context.inputRefs?.[refName]?.current) {
            return null;
        }

        if (FormValidationUtils.hasEmptyValue(value) && FormValidationUtils.getRolePermission({
            props: context.props,
            permission: config.permission || 'canEditAge'
        })) {
            return config.requiredMessage || FormValidationUtils.buildRequiredMessage(definition);
        }

        return typeof context.validateAge === 'function' ? context.validateAge(value) : null;
    },

    interests({
        config,
        context,
        value
    }) {

        if (config.signupOnly !== false && !context.isSignupPage) {
            return null;
        }

        return FormValidationUtils.validators.arrayLength({
            config: {
                min: 1,
                max: config.limit || INTERESTS.LIMIT,
                minMessage: config.requiredMessage || 'Start typing to find and select at least one Interest',
                maxMessage: config.maxMessage || `Sorry, you have exceeded the maximum number of ${config.limit || INTERESTS.LIMIT} Interests. Please remove some before adding new Interests`
            },
            context,
            value
        });
    }
};

UserInputsValidationUtils.validators = {
    ...FormValidationUtils.validators,
    ...userInputsValidators
};

UserInputsValidationUtils.buildValidators = (validators = {}) => ({
    ...UserInputsValidationUtils.validators,
    ...validators
});

UserInputsValidationUtils.runConfiguredValidation = ({
    definition = {},
    value,
    context,
    validators = {}
}) => FormValidationUtils.runConfiguredValidation({
    definition,
    value,
    context,
    validators: UserInputsValidationUtils.buildValidators(validators)
});

UserInputsValidationUtils.runFieldValidation = ({
    definition = {},
    value,
    context,
    validators = {}
}) => FormValidationUtils.runFieldValidation({
    definition,
    value,
    context,
    validators: UserInputsValidationUtils.buildValidators(validators)
});
